import {
  Button,
  Code,
  Group,
  PinInput,
  Stack,
  Text,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { IconCopy } from '@tabler/icons-react';
import { useState } from 'react';
import QRCode from 'react-qr-code';

const TOTP_ISSUER = 'QDoc Billing';

export const buildTOTPSetupURI = (email: string, secret: string) =>
  `otpauth://totp/${encodeURIComponent(TOTP_ISSUER)}:${encodeURIComponent(
    email
  )}?secret=${encodeURIComponent(secret)}&issuer=${encodeURIComponent(
    TOTP_ISSUER
  )}`;

interface TOTPSetupProps {
  email: string;
  secret: string;
  error?: string | null;
  submitting?: boolean;
  onCancel: () => void;
  onVerify: (code: string) => Promise<void>;
}

export const TOTPSetup = ({
  email,
  secret,
  error,
  submitting = false,
  onCancel,
  onVerify,
}: TOTPSetupProps) => {
  const theme = useMantineTheme();
  const computedColorScheme = useComputedColorScheme('dark');
  const [code, setCode] = useState('');
  const [copied, setCopied] = useState(false);
  const setupURI = buildTOTPSetupURI(email, secret);

  const copySecret = async () => {
    await navigator.clipboard.writeText(secret);
    setCopied(true);
  };

  return (
    <Stack align="center">
      <Text size="sm">
        Scan this QR code with your authenticator app, or enter the setup key
        manually.
      </Text>
      <div
        aria-label="Authenticator app QR code"
        style={{
          background: theme.white,
          padding: 'var(--mantine-spacing-md)',
        }}
      >
        <QRCode
          value={setupURI}
          size={192}
          bgColor={theme.white}
          fgColor={theme.black}
        />
      </div>
      <Text size="sm" fw={500}>
        Manual setup key
      </Text>
      <Group gap="xs">
        <Code data-testid="totp-manual-secret">{secret}</Code>
        <Button
          aria-label="Copy setup key"
          leftSection={<IconCopy size={16} />}
          onClick={copySecret}
          size="xs"
          variant="light"
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </Group>
      <Text size="sm">Enter the current six-digit code to finish setup.</Text>
      <PinInput
        aria-label="Authenticator code"
        length={6}
        oneTimeCode
        type="number"
        value={code}
        onChange={(value) => setCode(value.replace(/[\s-]/g, ''))}
        onPaste={(event) => {
          const normalizedCode = event.clipboardData
            .getData('text')
            .replace(/[\s-]/g, '');
          if (/^\d{6}$/.test(normalizedCode)) {
            event.preventDefault();
            setCode(normalizedCode);
          }
        }}
      />
      {error && (
        <Text c={`red-${computedColorScheme}`} size="sm" role="alert">
          {error}
        </Text>
      )}
      <Group justify="flex-end" w="100%">
        <Button variant="default" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={() => onVerify(code)}
          disabled={!/^\d{6}$/.test(code)}
          loading={submitting}
        >
          Verify and enable
        </Button>
      </Group>
    </Stack>
  );
};
